/* eslint-disable @nx/enforce-module-boundaries */
import { Box, Divider, HStack, Heading, Link, Stack, Text } from '@mybridge/ui';
import moment from 'moment';
import { forwardRef, useContext, useMemo, useState } from 'react';
import { PublicProfileContext } from '../context';
import { ShowAllLink } from '../show-all-link';
import { MyNetworkContext } from 'v4/components/network/context';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useSelector } from 'react-redux';
import { CertificateIcon } from '@mybridge/icons';

export const MyProfileCertificate = forwardRef(({ ...props }, ref) => {
  const { certificates, entityHasStartDates, entityHasEndDates, publicProfile } =
    useContext(PublicProfileContext);
  const { t } = useTranslator();
  const { isFriend } = useContext(MyNetworkContext);
  const [showAll, setShowAll] = useState(false);
  const { userProfileInfo } = useSelector((state) => state.userProfile);

  // Check if this is the user's own profile
  const isOwnProfile = publicProfile?.id === userProfileInfo?.id;

  // Filter certificates based on audience visibility
  const visibleCertificates = useMemo(() => {
    return certificates?.filter(cert => 
      cert?.audience === 1 || (cert?.audience === 2 && isFriend(publicProfile))
    ) ?? [];
  }, [certificates, isFriend, publicProfile]);

  // Check if all certificates are private (audience 2 or 3) and user is not a friend
  const allCertificatesPrivate = useMemo(() => {
    return certificates?.length > 0 && 
           certificates?.every(cert => cert?.audience === 2 || cert?.audience === 3) &&
           !isFriend(publicProfile) &&
           !isOwnProfile;
  }, [certificates, isFriend, publicProfile, isOwnProfile]);

  // If no certificates at all, return empty fragment
  if (!certificates?.length) {
    return <></>;
  }

  // Determine if we should show the heading - similar to experience, education, and skills components
  const shouldShowHeading = !allCertificatesPrivate && visibleCertificates?.length > 0;

  return (
    <>
      <Stack>
        {/* Only show heading if we have certificates to display */}
        {shouldShowHeading && (
          <HStack alignItems="flex-start">
            <Stack flex={1}>
              <Heading fontSize="18px" color="brandPrimary.500" fontWeight="400">
                {t('profile.certificationsAwards') || "Certifications & Awards"}
              </Heading>
            </Stack>
          </HStack>
        )}
        
        {allCertificatesPrivate ? (
          <HStack>
            <CertificateIcon color="#868585" />
            <Text color="#868585">
              {t('otherModules.noCertificationsToShow') || "No certifications to show"}
            </Text>
          </HStack>
        ) : visibleCertificates?.length ? (
          <>
            <Stack pt={4} spacing={6} pl={3}>
              {visibleCertificates
                ?.filter?.((_, ind) => showAll || ind < 3)
                ?.map?.((ms, msIndex) => {
                  return (
                    <Stack key={msIndex}>
                      <HStack key={msIndex} alignItems="flex-start">
                        <Stack spacing={0} flex={1}>
                          <Box>
                            <Heading size="sm" fontWeight="600">{ms?.cert_name}</Heading>
                          </Box>
                          {entityHasStartDates(ms) || entityHasEndDates(ms) ? (
                            <>
                              <Box fontSize="sm">
                                <Text display="inline">
                                  {moment()
                                    .set(
                                      'M',
                                      parseInt(ms?.start_month ?? '1') - 1
                                    )
                                    ?.format?.('MMMM')}
                                  ,&nbsp;
                                  {moment()
                                    .set('Y', ms?.start_year)
                                    ?.format?.('YYYY')}{' '}
                                </Text>
                                {ms?.cert_not_expire ? (
                                  ''
                                ) : entityHasEndDates(ms) ? (
                                  <>
                                    <Text display="inline"> - </Text>
                                    <Text display="inline">
                                      {moment()
                                        .set(
                                          'M',
                                          parseInt(ms?.end_month ?? '1') - 1
                                        )
                                        ?.format?.('MMMM')}
                                      ,&nbsp;
                                      {moment()
                                        .set('Y', ms?.end_year)
                                        ?.format?.('YYYY')}{' '}
                                    </Text>
                                  </>
                                ) : (
                                  ''
                                )}
                              </Box>
                            </>
                          ) : (
                            ''
                          )}

                          <Stack spacing={0}>
                            {ms?.cert_authority ? (
                              <Text>{ms?.cert_authority}</Text>
                            ) : (
                              ''
                            )}
                            {ms?.license_number ? (
                              <Text>{ms?.license_number}</Text>
                            ) : (
                              ''
                            )}
                            {ms?.cert_url ? (
                              <Link
                                isExternal
                                href={
                                  ms?.cert_url.startsWith('http')
                                    ? ms?.cert_url
                                    : `https://${ms?.cert_url.includes('www.') ? ms?.cert_url : `www.${ms?.cert_url}`}`
                                }
                              >
                                {ms?.cert_url.includes('www.') ? ms?.cert_url : `www.${ms?.cert_url}`}
                              </Link>
                            ) : (
                              ''
                            )}
                          </Stack>
                        </Stack>
                      </HStack>
                      {visibleCertificates?.length > 1 &&
                      msIndex < visibleCertificates?.length - 1 ? (
                        <Divider />
                      ) : (
                        ''
                      )}
                    </Stack>
                  );
                })}
            </Stack>
            {!showAll && visibleCertificates?.length > 3 ? (
              <ShowAllLink onClick={(e) => setShowAll(true)} />
            ) : (
              ''
            )}
          </>
        ) : (
          <>
            <HStack>
              {isOwnProfile ? (
                <Link>{t('otherModules.noCertificationsToShow') || "No certifications to show"}</Link>
              ) : (
                <HStack>
                  <CertificateIcon color="#868585" />
                  <Text color="#868585">
                    {t('otherModules.noCertificationsToShow') || "No certifications to show"}
                  </Text>
                </HStack>
              )}
            </HStack>
          </>
        )}
      </Stack>
    </>
  );
});
